import { TenFourText } from '@/components/TenFourText'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'
import { indefiniteArticle } from '@tenfour/contracts'
import { useAuth } from '@/components/auth/AuthContext'
import { useTierCatalog } from '@/modules/billing/hooks'
import { pitchFor, priceFor, tierLabelFrom, TIER_STATIC } from '@/modules/billing/tier-pitch'
import { useModalA11y } from '@/hooks/useModalA11y'
import s from './UpgradeModal.module.css'

export interface UpgradeModalOptions {
  /** Tier the locked feature needs — picks the persona image + pitch copy. */
  requiredTier: 'PRO' | 'MAX'
  /** Short name of the locked feature, e.g. "Birthday texts". */
  feature?: string
}

interface UpgradeModalContextValue {
  openUpgradeModal: (options: UpgradeModalOptions) => void
}

const UpgradeModalContext = createContext<UpgradeModalContextValue | null>(null)

/**
 * The button that owns the menu `el` is an item of: the [aria-haspopup]
 * control beside a role="menu" list (the sidebar org menu), or null.
 */
function menuButtonOf(el: Element | null): HTMLElement | null {
  const button = el?.closest('[role="menu"]')?.parentElement?.querySelector(':scope > [aria-haspopup]')
  return button instanceof HTMLElement ? button : null
}

/**
 * Hook every tier-locked interaction calls: `openUpgradeModal({ requiredTier:
 * 'MAX', feature: 'Birthday texts' })` pops the shared pitch modal with
 * a CTA into /upgrade. Provider is mounted once in the authenticated layout.
 */
export function useUpgradeModal(): UpgradeModalContextValue {
  const ctx = useContext(UpgradeModalContext)
  if (!ctx) throw new Error('useUpgradeModal must be used within UpgradeModalProvider')
  return ctx
}

export function UpgradeModalProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<UpgradeModalOptions | null>(null)
  const navigate = useNavigate()
  const { accountId } = useAuth()
  // Price AND pitch copy resolve from the runtime tier catalog (shared with
  // /upgrade via modules/billing/tier-pitch.ts) so the claims here can't
  // contradict the configured allowances/features (#302).
  const catalog = useTierCatalog(accountId)

  // Opened from a menu item (org menu → Add organization), the opener hides as
  // its menu closes, so focus can't go back to it. Hand focus to the menu's
  // button first, as in the ARIA menu pattern: useModalA11y records that as
  // the opener, and gets it again as the stand-in if the opener has gone.
  const menuButtonRef = useRef<HTMLElement | null>(null)
  const openUpgradeModal = useCallback((o: UpgradeModalOptions) => {
    const menuButton = menuButtonOf(document.activeElement)
    menuButton?.focus({ preventScroll: true })
    menuButtonRef.current = menuButton
    setOptions(o)
  }, [])
  const close = useCallback(() => setOptions(null), [])
  const value = useMemo(() => ({ openUpgradeModal }), [openUpgradeModal])

  // Focus trap + Escape + scroll lock + focus restore (DES-08).
  const dialogRef = useModalA11y<HTMLDivElement>(Boolean(options), close, {
    returnFocus: () => menuButtonRef.current,
  })

  const tierStatic = options ? TIER_STATIC[options.requiredTier] : null
  // The runtime catalog is the only authority on the tier's NAME; the
  // persona image and the price fallback above stay static. Undefined until it
  // resolves — the copy below has a tier-neutral form for that window, the same
  // sentence TierGate shows.
  const tierLabel = options ? tierLabelFrom(catalog.data?.tiers, options.requiredTier) : undefined
  const pitch = options ? pitchFor(catalog.data?.tiers, options.requiredTier) : null
  const pitchPriceUsd = options ? priceFor(catalog.data?.tiers, options.requiredTier) : null

  return (
    <UpgradeModalContext.Provider value={value}>
      {children}
      {options && pitch && tierStatic ? (
        <div className={s.overlay} onClick={close} role="presentation">
          <div
            ref={dialogRef}
            className={s.modal}
            role="dialog"
            aria-modal="true"
            aria-label={tierLabel ? `Upgrade to ${tierLabel}` : 'Upgrade your plan'}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className={s.closeBtn} onClick={close} aria-label="Close">
              <X size={18} />
            </button>
            <img src={tierStatic.image} alt="" className={s.avatar} />
            <h2 className={s.title}>
              {options.feature
                ? tierLabel
                  ? `${options.feature} is ${indefiniteArticle(tierLabel)} ${tierLabel} feature`
                  : `${options.feature} requires a higher plan`
                : tierLabel
                  ? `Upgrade to ${tierLabel}`
                  : 'Upgrade your plan'}
            </h2>
            <p className={s.tagline}><TenFourText>{pitch.tagline}</TenFourText></p>
            {pitch.inheritsFrom ? (
              <p className={s.inherits}>Everything in {pitch.inheritsFrom}, plus:</p>
            ) : null}
            <ul className={s.bullets}>
              {pitch.bullets.map((b) => (
                <li key={b.key}><TenFourText>{b.label}</TenFourText></li>
              ))}
            </ul>
            <button
              type="button"
              className={s.cta}
              onClick={() => {
                close()
                navigate('/upgrade')
              }}
            >
              See plans — ${pitchPriceUsd}/mo
            </button>
            <button type="button" className={s.dismiss} onClick={close}>
              Not now
            </button>
          </div>
        </div>
      ) : null}
    </UpgradeModalContext.Provider>
  )
}
